import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { GameCatalogEntry } from '@perplexicon/api-client';

import { useCreateSoloSession } from './games.js';

export interface StartGameFlow {
  createSolo: ReturnType<typeof useCreateSoloSession>;
  /** Whether the given game's solo session is the one currently being created. */
  isStarting: (gameId: string) => boolean;
  startSolo: (gameId: string) => void;
  versusGame: GameCatalogEntry | null;
  setVersusGame: (game: GameCatalogEntry | null) => void;
  lobbyGame: GameCatalogEntry | null;
  setLobbyGame: (game: GameCatalogEntry | null) => void;
}

/**
 * The "start a game" flow shared by the dashboard's `ChooseGameSection` and
 * the catalog page's `StartAGameSection` — the solo-create mutation and
 * navigate-on-success, retry-on-active-game-conflict (`onConflict`), and the
 * versus/lobby dialog open state a card's "Play versus"/"Host lobby" action
 * sets. Both call sites had this exact logic typed out twice, byte for byte.
 *
 * Deliberately does NOT own dialog rendering or the catalog page's
 * URL-driven versus-dialog deep link (`?create=multiplayer&game=<id>`) —
 * only the catalog page has that URL contract, and folding it into a hook
 * every caller shares would leak it into the dashboard too, which has no
 * such link to honor.
 */
export function useStartGameFlow(
  onConflict: (error: unknown, retry: () => void) => boolean,
): StartGameFlow {
  const createSolo = useCreateSoloSession();
  const navigate = useNavigate();
  const [versusGame, setVersusGame] = useState<GameCatalogEntry | null>(null);
  const [lobbyGame, setLobbyGame] = useState<GameCatalogEntry | null>(null);

  function startSolo(gameId: string) {
    createSolo.mutate(gameId, {
      onSuccess: (session) => {
        void navigate(`/play/${session.id}`);
      },
      onError: (error) => onConflict(error, () => startSolo(gameId)),
    });
  }

  return {
    createSolo,
    isStarting: (gameId: string) => createSolo.isPending && createSolo.variables === gameId,
    startSolo,
    versusGame,
    setVersusGame,
    lobbyGame,
    setLobbyGame,
  };
}
